// PendientesCompletarCard — horas que registró la MÁQUINA y esperan lo que
// solo una persona sabe: el lote/OP y cuántos operarios hubo (contrato de
// ingesta §7). "Confirmar" envía PATCH /api/registros/:id/completar.
//
// Completar no es corregir: no pide razón. Si la persona cambia el lote o los
// operarios de una hora ya validada, el API la devuelve a la cola de
// validación (igual que la edición del líder).
//
// Presentacional: la página entrega las horas, el id que se está guardando y
// el callback. `soloLectura` (Ver como) oculta los controles.

import { useState } from 'react';
import type { RegistroCompletar, RegistroPendienteCompletar } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Icon } from '../../components/ui/icon';
import { Input } from '../../components/ui/input';
import { useTenantConfig } from '../../lib/api/useTenantConfig';
import { placeholderLote, validarLoteUi } from '../../lib/formatoLote';
import { useTouchTargets } from '../../components/ui/touch-targets';
import { formatearMiles } from './shared';

export interface PendientesCompletarCardProps {
  registros: RegistroPendienteCompletar[];
  soloLectura: boolean;
  /** Id de la hora cuyo PATCH está en curso (deshabilita su botón). */
  guardandoId: string | null;
  onConfirmar: (registroId: string, body: RegistroCompletar) => void;
  /** Muestra el nombre de la línea en cada fila (supervisor con varias líneas). */
  mostrarLinea?: boolean;
}

export function PendientesCompletarCard({
  registros,
  soloLectura,
  guardandoId,
  onConfirmar,
  mostrarLinea = false,
}: PendientesCompletarCardProps) {
  if (registros.length === 0) return null;
  return (
    <Card variant="warn-left" aria-label="Horas de máquina por completar">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2">
          <Icon name="plug" size={15} stroke={2} className="text-warn" />
          {registros.length === 1
            ? 'Una hora de máquina por completar'
            : `${registros.length} horas de máquina por completar`}
        </CardTitle>
        <p className="text-xs text-fg-tertiary">
          La máquina contó la producción. Confirme el lote y los operarios de cada hora.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {registros.map((r) => (
          <FilaCompletar
            key={r.id}
            registro={r}
            soloLectura={soloLectura}
            guardando={guardandoId === r.id}
            bloqueado={guardandoId !== null && guardandoId !== r.id}
            mostrarLinea={mostrarLinea}
            onConfirmar={onConfirmar}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function FilaCompletar({
  registro: r,
  soloLectura,
  guardando,
  bloqueado,
  mostrarLinea,
  onConfirmar,
}: {
  registro: RegistroPendienteCompletar;
  soloLectura: boolean;
  guardando: boolean;
  bloqueado: boolean;
  mostrarLinea: boolean;
  onConfirmar: (registroId: string, body: RegistroCompletar) => void;
}) {
  const touch = useTouchTargets();
  const [lote, setLote] = useState(r.op_lote_oc_cliente ?? '');
  const [operarios, setOperarios] = useState(
    r.numero_operarios > 0 ? String(r.numero_operarios) : '',
  );
  const [error, setError] = useState<string | null>(null);
  const { formatoLote } = useTenantConfig();
  const idBase = `completar-${r.id}`;

  function confirmar() {
    setError(null);
    const n = Number(operarios);
    if (!operarios.trim() || !Number.isInteger(n) || n < 1 || n > 60) {
      setError('Indique los operarios (número entero de 1 a 60)');
      return;
    }
    const loteLimpio = lote.trim();
    if (loteLimpio.length > 32) {
      setError('El lote admite máximo 32 caracteres');
      return;
    }
    // Solo se envía (y se valida contra el formato de la empresa, B7) si el
    // líder lo cambió: el API conserva el lote de la máquina si no llega.
    const loteCambio = loteLimpio !== '' && loteLimpio !== (r.op_lote_oc_cliente ?? '');
    if (loteCambio) {
      const loteOk = validarLoteUi(loteLimpio, formatoLote);
      if (loteOk !== true) {
        setError(loteOk);
        return;
      }
    }
    onConfirmar(r.id, {
      confirmar: true,
      numero_operarios: n,
      ...(loteCambio ? { op_lote_oc_cliente: loteLimpio } : {}),
    });
  }

  return (
    <div className="flex flex-col gap-2 rounded border border-border bg-surface-elevated px-3 py-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-sm font-medium text-fg">
          {r.hora_inicio_bloque}–{r.hora_fin_bloque}
          <span className="font-normal text-fg-secondary"> · {r.sku_descripcion}</span>
        </span>
        <span className="text-xs tabular-nums text-fg-secondary">
          {formatearMiles(r.cantidad_procesada)} u
          {r.cantidad_kg !== null ? ` · ${r.cantidad_kg.toLocaleString('es-CO')} kg` : ''}
          {mostrarLinea ? ` · ${r.linea_nombre}` : ''}
        </span>
      </div>
      {r.paros_pendientes_causal > 0 ? (
        <Badge tone="warn" className="self-start normal-case tracking-normal">
          {r.paros_pendientes_causal === 1
            ? 'Esta hora tiene un paro sin causal'
            : `Esta hora tiene ${r.paros_pendientes_causal} paros sin causal`}
        </Badge>
      ) : null}
      {soloLectura ? (
        <p className="text-xs text-fg-tertiary">
          Lote: {r.op_lote_oc_cliente ?? '—'} · Operarios:{' '}
          {r.numero_operarios > 0 ? r.numero_operarios : '—'}
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex min-w-[9rem] flex-1 flex-col gap-1 text-[11px] text-fg-tertiary">
            OP / Lote
            <Input
              id={`${idBase}-lote`}
              aria-label={`Lote de la hora ${r.hora_inicio_bloque}`}
              value={lote}
              maxLength={32}
              placeholder={placeholderLote(formatoLote)}
              onChange={(e) => setLote(e.target.value)}
              className={touch ? 'h-11' : 'h-9'}
            />
          </label>
          <label className="flex w-24 flex-col gap-1 text-[11px] text-fg-tertiary">
            Operarios
            <Input
              id={`${idBase}-operarios`}
              aria-label={`Operarios de la hora ${r.hora_inicio_bloque}`}
              type="number"
              inputMode="numeric"
              min={1}
              max={60}
              value={operarios}
              onChange={(e) => setOperarios(e.target.value)}
              className={touch ? 'h-11 tabular-nums' : 'h-9 tabular-nums'}
            />
          </label>
          <Button
            type="button"
            size={touch ? 'touch' : 'sm'}
            className={touch ? undefined : 'h-9'}
            loading={guardando}
            disabled={bloqueado}
            onClick={confirmar}
          >
            Confirmar
          </Button>
        </div>
      )}
      {error ? (
        <p role="alert" className="text-xs text-crit">
          {error}
        </p>
      ) : null}
    </div>
  );
}
